/**
 * Garage Metafield Sync
 *
 * Persists logged-in garage changes to the customer garage Cloud Run API.
 * localStorage.searchTerms remains the UI/runtime cache; guests stay local-only.
 */
(function () {
	"use strict";

	const STORAGE_KEY = "searchTerms";
	const OWNER_KEY = "partifyGarageOwner";
	const GUEST_OWNER = "guest";
	const FITMENT_KEYS = [
		"easysearch-preselect-fitment",
		"easysearch-preselect",
	];
	const UPDATE_URL =
		"https://update-customer-account-info-49754682551.us-east5.run.app";
	const SYNC_DELAY = 250;

	let syncTimer = null;
	let lastSyncedData = "";
	let guestCarryoverActiveVehicle = null;
	let guestCarryoverCustomerId = "";

	function getCustomerId() {
		const id =
			window.igCustomerId ||
			(window.__PARTIFY_CUSTOMER__ && window.__PARTIFY_CUSTOMER__.id);

		return id ? String(id) : "";
	}

	function getCustomerOwner(customerId) {
		return customerId ? `customer:${customerId}` : "";
	}

	function getCurrentOwner() {
		const customerId = getCustomerId();
		return window.igCustomerLoggedIn && customerId
			? getCustomerOwner(customerId)
			: GUEST_OWNER;
	}

	function clearActiveGarage() {
		localStorage.setItem(STORAGE_KEY, "[]");
		FITMENT_KEYS.forEach((key) => {
			localStorage.setItem(key, JSON.stringify({ value: "" }));
		});
		try {
			sessionStorage.setItem("es_force_clear", "1");
		} catch (error) {
			// The active Garage projection is already cleared from localStorage.
		}
	}

	function initializeGarageScope() {
		const currentOwner = getCurrentOwner();
		try {
			const storedOwner = localStorage.getItem(OWNER_KEY) || "";
			const storedGarage = localStorage.getItem(STORAGE_KEY);
			const hasUnownedActiveGarage =
				!storedOwner && storedGarage !== null && storedGarage !== "[]";
			const isSameOwner = storedOwner === currentOwner;
			const isGuestToCustomer =
				storedOwner === GUEST_OWNER && currentOwner.startsWith("customer:");

			if (isGuestToCustomer && storedGarage) {
				try {
					const storedVehicles = JSON.parse(storedGarage);
					if (Array.isArray(storedVehicles) && storedVehicles.length) {
						guestCarryoverActiveVehicle = storedVehicles[0];
						guestCarryoverCustomerId = getCustomerId();
					}
				} catch (error) {
					// Malformed guest state is not eligible for active-vehicle carry-over.
				}
			}

			if (
				hasUnownedActiveGarage ||
				(storedOwner && !isSameOwner && !isGuestToCustomer)
			) {
				clearActiveGarage();
			}
			localStorage.setItem(OWNER_KEY, currentOwner);
		} catch (error) {
			console.error("[GarageScope] Failed to initialize active Garage scope");
		}
	}

	function isCurrentCustomerOwner(customerId) {
		if (!window.igCustomerLoggedIn || !customerId) return false;
		try {
			return (
				localStorage.getItem(OWNER_KEY) === getCustomerOwner(String(customerId))
			);
		} catch (error) {
			return false;
		}
	}

	function getGuestCarryoverActiveVehicle(customerId) {
		const requestedCustomerId = customerId ? String(customerId) : "";
		if (
			!guestCarryoverActiveVehicle ||
			!requestedCustomerId ||
			requestedCustomerId !== guestCarryoverCustomerId ||
			!isCurrentCustomerOwner(requestedCustomerId)
		) {
			return null;
		}

		return guestCarryoverActiveVehicle;
	}

	window.PartifyGarageScope = {
		ownerKey: OWNER_KEY,
		getCurrentOwner,
		getGuestCarryoverActiveVehicle,
		isCurrentCustomerOwner,
	};

	initializeGarageScope();

	function shouldSync() {
		const customerId = getCustomerId();
		return Boolean(
			window.igCustomerLoggedIn &&
				customerId &&
				isCurrentCustomerOwner(customerId),
		);
	}

	function readVehicles() {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (raw === null) return null;

		try {
			const vehicles = JSON.parse(raw);
			return Array.isArray(vehicles) ? vehicles : [];
		} catch (error) {
			console.error("[GarageSync] Failed to read garage cache:", error);
			return [];
		}
	}

	function formatGarageData(vehicles) {
		if (typeof window.formatSearchTermsForJSON === "function") {
			return window.formatSearchTermsForJSON(vehicles);
		}

		return {
			vehicles: vehicles
				.filter((vehicle) => vehicle && typeof vehicle === "object")
				.map((vehicle) => {
					const year =
						typeof vehicle.year === "string" && /^\d+$/.test(vehicle.year)
							? Number(vehicle.year)
							: (vehicle.year ?? null);

					return {
						year,
						make: vehicle.make ?? null,
						model: vehicle.model ?? null,
						submodel: vehicle.submodel ?? null,
						engine: vehicle.engine ?? null,
						bodyType: vehicle.bodyType ?? null,
						vin: vehicle.vin ?? null,
						plate: vehicle.plate ?? null,
						state: vehicle.state ?? null,
						paintCode: vehicle.paintCode ?? null,
						decodedByBumper: vehicle.decodedByBumper ?? null,
						lastCollectionImage: vehicle.lastCollectionImage ?? null,
						ymm: vehicle.ymm ?? null,
						troublesome_format: vehicle.troublesome_format ?? null,
						troublesome_description:
							vehicle.troublesome_description ?? null,
						...(vehicle.wmiCountry ? { wmiCountry: vehicle.wmiCountry } : {}),
					};
				}),
		};
	}

	async function updateCustomerGarage(garageData) {
		if (typeof window.updateCustomerAccountGarage === "function") {
			return window.updateCustomerAccountGarage(garageData);
		}

		const customerId = getCustomerId();
		const response = await fetch(UPDATE_URL, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				customerId,
				garageData,
			}),
		});

		let data = null;
		try {
			data = await response.json();
		} catch (error) {
			// Keep the original response status as the useful failure signal.
		}

		if (!response.ok) {
			const error = new Error("Customer garage update API error");
			error.status = response.status;
			error.raw = data;
			throw error;
		}

		return data;
	}

	async function syncGarage() {
		syncTimer = null;

		if (!shouldSync()) return;

		const vehicles = readVehicles();
		if (vehicles === null) return;

		const garageData = formatGarageData(vehicles);
		const serialized = JSON.stringify(garageData);
		if (serialized === lastSyncedData) return;

		try {
			await updateCustomerGarage(garageData);
			lastSyncedData = serialized;
			console.log("[GarageSync] Synced garage to customer account");
		} catch (error) {
			console.error("[GarageSync] Customer garage update failed:", error);
		}
	}

	function queueSync(event) {
		const detail = event && event.detail;
		if (
			detail &&
			detail.source === "account-hydration" &&
			!detail.shouldSyncCustomerGarage
		) {
			return;
		}

		if (!shouldSync()) return;
		if (syncTimer) clearTimeout(syncTimer);
		syncTimer = setTimeout(syncGarage, SYNC_DELAY);
	}

	window.addEventListener("searchTermsUpdated", queueSync);
	window.addEventListener("garageUpdated", queueSync);
})();
